import React, { useEffect, useRef } from 'react';
import {
    Table, Whisper, Popover, Checkbox as RSCheckbox,
} from 'rsuite';
import './weeklyShiftLineTable.scss';
import {
    Button,
    Paper,
    Grid,
} from '@material-ui/core';
import {
    ChevronLeft,
    ChevronRight,
    SwapHoriz,
} from '@material-ui/icons';

import ShiftToolTip from '../ShiftToolTip';
// eslint-disable-next-line no-unused-vars
import { ShiftLineTableProps, ShiftLineTableRow, ShiftLineTableRowDay } from './typing';
import useVirtualization, { HiddenBox } from '../../hooks/useVirtualization';

export const DaysOfWeekNames = Object.freeze({
    0: 'Sunday',
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday',
});

const { Column, HeaderCell, Cell } = Table;
const RSuiteTableRowDefaultHeight = 46;

/** @param {{rowData: ShiftLineTableRow}} rowData */
const JobCell = ({ rowData, ...props }) => {
    const tooltipStatus = (
        <Popover visible>
            {rowData.orgJobPath}
        </Popover>
    );
    return (
        <Cell {...props} >
            <span className="wslt_orgJob_text">
                <Whisper
                    placement="top"
                    delay={500}
                    delayOpen={500}
                    delayClose={500}
                    speaker={tooltipStatus}
                >
                    <span className="wslt-cell-text-content">
                        {' '}
                        {rowData.orgJobPath}
                        {' '}
                    </span>
                </Whisper>
            </span>

        </Cell>
    );
};

/**
 * @param {ShiftLineTableRow[]} rows
 */
const getRowHeight = (rows) => {
    const maxShiftsPerWeek = Math.max(...rows.map((x) => Math.max(...x.days.map((y) => y.shifts.length))));
    return (RSuiteTableRowDefaultHeight + 24 * (maxShiftsPerWeek - 1));
};

/**
 * yarn lint won't work, extrated this in a function
 * @param {{ rowName: string }} row
 * @param {{ name: string }} x
 * @returns
 */
function getKey(row, x) {
    return `shift_element_${row.rowName}_${x.name}`;
}

/**
 * @param {ShiftLineTableProps} props
 */
function WeeklyShiftLineTable(props) {
    const {
        rowsProp, firstDayOfTheWeek = 0, handleCheck = () => { }, handleCheckAll = () => { }, isCheckable = false, isCheckboxDisabled = false, tableHeight = 400, renderEmpty, dayCount, isPeriodTypeDay, daysPerPeriod = 7,
    } = props;
    const [weekNumber, setWeekNumber] = React.useState(0);
    const [rows, setRows] = React.useState(rowsProp);
    const week = Math.ceil(dayCount / 7);
    const [nbWeeks, setNbWeeks] = React.useState(week);
    const { debounceRender, randomValue } = useVirtualization();

    useEffect(() => {
        const maximumWeekDays = Math.max(...rowsProp.map((a) => a.days.length));
        const weeks = Math.ceil(maximumWeekDays / daysPerPeriod);

        if (weekNumber + 1 > weeks) {
            setWeekNumber(weeks);
        }
        setRows(rowsProp);
        setNbWeeks(weeks);

        // We only  want to reset if the number of weeks is smaller than the selected week
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(rowsProp)]);

    /**
     * @param {ShiftLineTableRow} row
     * @param {ShiftLineTableRowDay} dayElement
     * @returns
     */
    const GenerateDayElements = (row, dayElement) => {
        const shifts = dayElement?.shifts?.map((x) => {
            const key = getKey(row, x);
            const shiftNameSpan = (
                <span
                    key={key}
                    className="wslt_shiftElement"
                >
                    {x.name}
                    {x.tooltipShiftInfo?.hasTransfer && <SwapHoriz className="wslt_transfer_icon" />}
                </span >
            );

            if (x.tooltipShiftInfo) {
                return (
                    <ShiftToolTip
                        element={shiftNameSpan}
                        shiftInfos={x.tooltipShiftInfo}
                    />
                );
            }

            return shiftNameSpan;
        });

        // Add paycodes code here when needed
        return shifts;
    };

    /**
     * @param {Number} direction
     */
    const handleWeekChange = (direction) => {
        if (direction > 0) {
            setWeekNumber(Math.min(weekNumber + 1, nbWeeks - 1));
        } else {
            setWeekNumber(Math.max(weekNumber - 1, 0));
        }
    };

    /**
     * @type {number[]}
     */
    const checkedRows = rows.filter((x) => x.isSelected).map((x) => x.id);

    const isSelectAllChecked = checkedRows.length === rows.length;
    const isSelectAllIndeterminate = !!(!isSelectAllChecked
        && checkedRows.length
        && checkedRows.length < rows.length);

    return (
        <Paper
            className="wslt_paper"
            key="weeklyShiftLinePaper"
        >
            <div className="wslt_weekSelectionBar">
                <Grid
                    container
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                    className="wslt_root"
                >
                    <Grid
                        xs={4}
                        item
                    >
                        {
                            nbWeeks > 1
                                ? (
                                    <Button onClick={() => handleWeekChange(-1)}>
                                        <ChevronLeft />
                                    </Button>
                                ) : null
                        }
                    </Grid>
                    <Grid
                        xs={4}
                        className="wslt_alignCenter"
                        item
                    >
                        {isPeriodTypeDay ? `Day ${((weekNumber + 1) * daysPerPeriod) - 6} to ${(weekNumber + 1) * daysPerPeriod}` : `Week ${weekNumber + 1}`}
                    </Grid>
                    <Grid
                        xs={4}
                        className="wslt_flexRight"
                        item
                    >
                        {
                            nbWeeks > 1
                                ? (
                                    <Button onClick={() => handleWeekChange(1)}>
                                        <ChevronRight />
                                    </Button>
                                ) : null
                        }

                    </Grid>
                </Grid>
            </div>
            <HiddenBox value={randomValue} />
            <Table
                virtualized
                height={tableHeight}
                data={rows}
                rowHeight={getRowHeight(rows)}
                renderEmpty={renderEmpty}
                onScroll={debounceRender}
                rowClassName="wslt_row"
                hover={false}
            >
                {isCheckable && <Column
                    width={50}
                    align="center"
                    resizable
                >
                    <HeaderCell>
                        <div style={{ lineHeight: '40px' }}>
                            <RSCheckbox
                                inline
                                checked={isSelectAllChecked}
                                indeterminate={isSelectAllIndeterminate}
                                onChange={handleCheckAll}
                                disabled={isCheckboxDisabled}
                            />
                        </div>
                    </HeaderCell>
                    <Cell>
                        {/** @param {ShiftLineTableRow} rowData */
                            (rowData) => (
                                <div style={{ lineHeight: '46px' }}>
                                    <RSCheckbox
                                        value={rowData.id}
                                        inline
                                        onChange={handleCheck}
                                        checked={checkedRows.includes(rowData.id)}
                                        disabled={isCheckboxDisabled}
                                    />
                                </div>
                            )
                        }
                    </Cell>
                </Column>}
                <Column
                    align="center"
                    verticalAlign="middle"
                    flexGrow={3}
                >
                    <HeaderCell>Shiftline</HeaderCell>
                    <Cell
                        className="wslt_tableCells wslt_text_cell"
                        dataKey="rowName"
                    />
                </Column>

                <Column
                    align="center"
                    flexGrow={2}
                    verticalAlign="middle"
                >
                    <HeaderCell>Jobs</HeaderCell>
                    <JobCell
                        className="wslt_tableCells wslt_text_cell"
                        dataKey="Name"
                    />
                </Column>
                {
                    Array.from(Array(daysPerPeriod).keys()).map((x) => (
                        <Column
                            flexGrow={2}
                        >
                            <HeaderCell
                                className={`${(weekNumber * daysPerPeriod) + (x + 1) > dayCount ? 'wslt_lightgrey' : ''}`}
                            >
                                {isPeriodTypeDay ? `Day ${(weekNumber * daysPerPeriod) + (x + 1)}` : DaysOfWeekNames[(x + firstDayOfTheWeek) % daysPerPeriod]}
                            </HeaderCell>
                            <Cell
                                className={`wslt_tableCells ${(weekNumber * daysPerPeriod) + (x + 1) > dayCount ? 'wslt_lightgrey' : ''}`}
                            >
                                {
                                    (/** @type {ShiftLineTableRow} */rowData) =>
                                        GenerateDayElements(rowData, rowData.days[(weekNumber * daysPerPeriod) + x])
                                }
                            </Cell>
                        </Column>
                    ))
                }
            </Table>
        </Paper >

    );
}

export default WeeklyShiftLineTable;
